import { Check, ExternalLink } from "lucide-react";
import type { ReactNode } from "react";
import { useId, useState } from "react";

import { trpc } from "../../trpc";

type TodoOwner = "organisers" | "adi";

interface AddressedItem {
  text: string;
  url?: string;
  /** ISO date or timestamp of when it went live. */
  date?: string;
}

interface TestStep {
  text: string;
  url?: string;
}

interface TodoItem {
  text: string;
  owner: TodoOwner;
}

/** A place to try something, with an optional line on what it is for. */
interface TryItLink {
  label: string;
  url: string;
  note?: string;
}

/**
 * The assistant's note as adminBoardStatus answers it. `testable` and
 * `todo` still accept the older bare-string form so a note written
 * before owners existed keeps rendering.
 */
interface BoardStatus {
  workingOn: string;
  addressed: AddressedItem[];
  links: TryItLink[];
  testable: (string | TestStep)[];
  todo: (string | { text: string; owner?: TodoOwner })[];
  updatedAt?: string;
}

const TEST_STEPS_SHOWN = 5;
const ADDRESSED_SHOWN = 6;

/** The panel's heading; there is only ever one panel on the page. */
const PANEL_TITLE_ID = "feedback-status-title";

function isString<T>(value: T): value is T & string {
  return typeof value === "string";
}

function toTestStep(item: string | TestStep): TestStep {
  return isString(item) ? { text: item } : item;
}

function toTodo(item: string | { text: string; owner?: TodoOwner }): TodoItem {
  return isString(item)
    ? { text: item, owner: "adi" }
    : { text: item.text, owner: item.owner ?? "adi" };
}

const DAY = {
  day: "numeric",
  month: "short",
} satisfies Intl.DateTimeFormatOptions;

const DAY_WITH_YEAR = {
  ...DAY,
  year: "numeric",
} satisfies Intl.DateTimeFormatOptions;

/** "9 Sept", with the year only when it isn't the year of `now`. */
function formatDay(iso: string, now = Date.now()): string {
  const date = new Date(iso);
  return date.toLocaleDateString(
    "en-AU",
    date.getFullYear() === new Date(now).getFullYear() ? DAY : DAY_WITH_YEAR,
  );
}

/** "just now", "4 minutes ago", "yesterday": enough for a status line. */
export function relativeTime(iso: string, now = Date.now()): string {
  const seconds = Math.max(
    0,
    Math.round((now - new Date(iso).getTime()) / 1000),
  );
  if (seconds < 60) {
    return "just now";
  }
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) {
    return minutes === 1 ? "a minute ago" : `${minutes} minutes ago`;
  }
  const hours = Math.round(minutes / 60);
  if (hours < 24) {
    return hours === 1 ? "an hour ago" : `${hours} hours ago`;
  }
  const days = Math.round(hours / 24);
  if (days === 1) {
    return "yesterday";
  }
  if (days < 14) {
    return `${days} days ago`;
  }
  return `on ${formatDay(iso, now)}`;
}

const FULL_ROW = "md:col-span-2 xl:col-span-3";

/** Body text inside the cards: 16px, as every paragraph in the app. */
const CARD_TEXT = "text-[16px] leading-[1.55] text-[color:var(--color-ink)]";

/** An inline link inside a card: a little padding so a thumb can land on it. */
const INLINE_LINK =
  "inline-flex items-center gap-1 py-1.5 text-[14px] font-semibold whitespace-nowrap text-[color:var(--color-amber)] underline underline-offset-2";

/**
 * The assistant's note: what it is on right now, where to try things,
 * what to test, what is live, what is still to do. Hidden until it has
 * written one. Each part is a small card so the whole reads at a glance
 * rather than as five paragraphs.
 */
export function StatusPanel() {
  const query = trpc.adminBoardStatus.useQuery(undefined, {
    retry: false,
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
    refetchIntervalInBackground: false,
  });
  const status: BoardStatus | null = query.data ?? null;
  if (!status) {
    return null;
  }
  const steps = status.testable.map(toTestStep);
  const todos = status.todo.map(toTodo);
  const waiting = todos.filter((item) => item.owner === "organisers");
  const mine = todos.filter((item) => item.owner === "adi");
  const bothTodoCards = waiting.length > 0 && mine.length > 0;

  return (
    <aside aria-labelledby={PANEL_TITLE_ID} className="surface mt-8 p-5 md:p-6">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <h2 id={PANEL_TITLE_ID} className="eyebrow">
          From Claude, with Adi
        </h2>
        {status.updatedAt ? (
          <p className="text-[13px] text-[color:var(--color-ink-soft)]">
            Updated {relativeTime(status.updatedAt)}
          </p>
        ) : null}
      </div>

      <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        <Card title="Right now" className={FULL_ROW}>
          <p className="text-[16px] leading-[1.6] text-[color:var(--color-ink)] md:text-[17px]">
            {status.workingOn || "Nothing in hand at the moment."}
          </p>
        </Card>

        {status.links.length > 0 ? (
          <Card title="Try it" count={status.links.length}>
            <ul className="flex flex-col gap-3">
              {status.links.map((link) => (
                <TryLink key={link.url} link={link} />
              ))}
            </ul>
          </Card>
        ) : null}

        {steps.length > 0 ? (
          <Card
            title="What to test"
            count={steps.length}
            className="xl:col-span-2"
          >
            <Expandable items={steps} shown={TEST_STEPS_SHOWN}>
              {(visible) => (
                <ol
                  className={`list-decimal space-y-2 pl-6 marker:font-semibold marker:text-[color:var(--color-ink-soft)] ${CARD_TEXT}`}
                >
                  {visible.map((step) => (
                    <li key={step.text} className="pl-1">
                      {step.text}
                      {step.url ? (
                        <>
                          {" "}
                          <a
                            href={step.url}
                            target="_blank"
                            rel="noreferrer"
                            className={INLINE_LINK}
                          >
                            Open
                            <ExternalLink size={14} aria-hidden="true" />
                          </a>
                        </>
                      ) : null}
                    </li>
                  ))}
                </ol>
              )}
            </Expandable>
          </Card>
        ) : null}

        {status.addressed.length > 0 ? (
          <Card
            title="Addressed"
            count={status.addressed.length}
            className={FULL_ROW}
          >
            <Expandable
              items={[...status.addressed].reverse()}
              shown={ADDRESSED_SHOWN}
            >
              {(visible) => (
                <ul className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
                  {visible.map((item) => (
                    <DoneCard key={item.text} item={item} />
                  ))}
                </ul>
              )}
            </Expandable>
          </Card>
        ) : null}

        {waiting.length > 0 ? (
          <Card
            title="Waiting on you"
            count={waiting.length}
            className={bothTodoCards ? undefined : FULL_ROW}
          >
            <TodoList items={waiting} />
          </Card>
        ) : null}

        {mine.length > 0 ? (
          <Card
            title="On Adi"
            count={mine.length}
            className={bothTodoCards ? "xl:col-span-2" : FULL_ROW}
          >
            <TodoList items={mine} />
          </Card>
        ) : null}
      </div>
    </aside>
  );
}

function Card({
  title,
  count,
  className,
  children,
}: {
  title: string;
  count?: number;
  className?: string;
  children: ReactNode;
}) {
  return (
    <section
      className={`min-w-0 rounded-md border border-[color:var(--color-line)] bg-[color:var(--color-bg)] p-4 ${className ?? ""}`}
    >
      <header className="flex items-center justify-between gap-2">
        <h3 className="text-[16px] leading-[1.35] font-semibold text-[color:var(--color-ink)]">
          {title}
        </h3>
        {count === undefined ? null : (
          <span className="badge border border-[color:var(--color-line-strong)] px-2.5 text-[color:var(--color-ink-soft)] tabular-nums">
            {count}
          </span>
        )}
      </header>
      <div className="mt-3">{children}</div>
    </section>
  );
}

/**
 * A place to try something. Its note shows as a line under the link,
 * because a tooltip never appears on a phone.
 */
function TryLink({ link }: { link: TryItLink }) {
  const noteId = useId();
  return (
    <li className="flex flex-col items-start gap-1">
      <a
        href={link.url}
        target="_blank"
        rel="noreferrer"
        aria-describedby={link.note ? noteId : undefined}
        className="inline-flex min-h-11 items-center gap-2 rounded-full border border-[color:var(--color-line-strong)] bg-[color:var(--color-paper)] px-4 text-[15px] font-semibold text-[color:var(--color-ink)] transition-colors hover:border-[color:var(--color-ink)]"
      >
        {link.label}
        <ExternalLink
          size={16}
          aria-hidden="true"
          className="text-[color:var(--color-ink-soft)]"
        />
      </a>
      {link.note ? (
        <p
          id={noteId}
          className="text-[14px] leading-[1.45] text-[color:var(--color-ink-soft)]"
        >
          {link.note}
        </p>
      ) : null}
    </li>
  );
}

/**
 * Shows the first `shown` items with a "Show all N" toggle for the
 * rest. Collapse state lives here and nowhere else.
 */
function Expandable<T>({
  items,
  shown,
  children,
}: {
  items: T[];
  shown: number;
  children: (visible: T[]) => ReactNode;
}) {
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? items : items.slice(0, shown);
  return (
    <>
      {children(visible)}
      {items.length > shown ? (
        <button
          type="button"
          aria-expanded={expanded}
          onClick={() => setExpanded((value) => !value)}
          className="mt-2 inline-flex min-h-11 items-center text-[14px] font-semibold text-[color:var(--color-amber)] underline underline-offset-2"
        >
          {expanded ? "Show fewer" : `Show all ${items.length}`}
        </button>
      ) : null}
    </>
  );
}

function DoneCard({ item }: { item: AddressedItem }) {
  const footer = item.url || item.date;
  return (
    <li className="flex flex-col rounded-md border border-[color:var(--color-line)] bg-[color:var(--color-paper)] px-3.5 py-3">
      <div className="flex gap-2.5">
        <Check
          size={16}
          aria-hidden="true"
          className="mt-1 shrink-0 text-[color:var(--color-success)]"
        />
        <p className={`min-w-0 [overflow-wrap:anywhere] ${CARD_TEXT}`}>
          {item.text}
        </p>
      </div>
      {footer ? (
        <div className="mt-1 flex items-center justify-between gap-2 pl-[26px] text-[13px] text-[color:var(--color-ink-soft)]">
          <span>{item.date ? formatDay(item.date) : ""}</span>
          {item.url ? (
            <a
              href={item.url}
              target="_blank"
              rel="noreferrer"
              className={INLINE_LINK}
            >
              See it
            </a>
          ) : null}
        </div>
      ) : null}
    </li>
  );
}

function TodoList({ items }: { items: TodoItem[] }) {
  return (
    <ul
      className={`list-disc space-y-2 pl-5 marker:text-[color:var(--color-ink-soft)] ${CARD_TEXT}`}
    >
      {items.map((item) => (
        <li key={item.text}>{item.text}</li>
      ))}
    </ul>
  );
}
